import { useRef, useState } from 'react';
import type { AdminSampleShot } from '../lib/types';
import { api } from '../lib/api';
import { Banner, Field, IconBtn, Text, slugify } from './ui';
import { useAdminContent } from './useAdminContent';
import { EditModal } from './EditModal';
import { useConfirm } from '../components/ConfirmDialog';
import { Loading } from '../components/Loading';

/**
 * Reference photos for the six-shot set on the vendor photo standard page.
 *
 * The file never reaches the API at its original size. A phone photo is several
 * megabytes of detail nobody can see in a 260px square, so it is drawn to a canvas at
 * 1200px and re-encoded as JPEG here, in the browser. What posts is typically 100–200KB,
 * which is why the endpoint can take a plain JSON data URL and VU needs no upload
 * middleware, no bucket and no shared disk.
 */
const MAX_EDGE = 1200;
const QUALITY = 0.82;
const MAX_UPLOAD_BYTES = 500 * 1024;

async function downscaleToDataUrl(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('This browser cannot resize the image.');
  // A transparent PNG would otherwise flatten to black once it is encoded as JPEG.
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();

  return canvas.toDataURL('image/jpeg', QUALITY);
}

const kb = (bytes: number) => `${Math.max(1, Math.round(bytes / 1024))}KB`;

export function SampleShotsAdmin() {
  const { bundle, loading, error, note, run, setError } = useAdminContent();
  const { confirm, dialog } = useConfirm();
  const [draft, setDraft] = useState<Partial<AdminSampleShot> | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // One input per card, so "replace" always targets the slot whose button was pressed.
  const inputs = useRef<Record<string, HTMLInputElement | null>>({});

  if (loading) return <Loading />;
  if (!bundle) return <Banner error={error ?? 'Could not load content.'} />;

  const shots = bundle.sampleShots ?? [];

  async function upload(shot: AdminSampleShot, file: File) {
    if (!file.type.startsWith('image/')) {
      setError('Choose an image file — PNG, JPEG or WebP.');
      return;
    }

    setBusy(shot._id);
    try {
      const dataUrl = await downscaleToDataUrl(file);
      // base64 is 4 bytes per 3 of image, so check the real size before the round trip.
      const bytes = Math.floor((dataUrl.length - dataUrl.indexOf(',') - 1) * 0.75);
      if (bytes > MAX_UPLOAD_BYTES) {
        setError(`That image is still ${kb(bytes)} after resizing. Crop it, or pick a simpler photo.`);
        return;
      }
      await run(() => api.uploadSampleShotImage(shot._id, dataUrl), `${shot.label} photo updated.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not read that image.');
    } finally {
      setBusy(null);
    }
  }

  async function save() {
    if (!draft?.label?.trim()) return;

    const confirmed = await confirm({
      title: draft._id ? 'Save this slot?' : 'Add this slot?',
      body: 'Vendors see the photo standard page immediately.',
      confirmLabel: draft._id ? 'Save changes' : 'Add slot',
    });
    if (!confirmed) return;

    const body = {
      slug: draft.slug?.trim() || slugify(draft.label),
      label: draft.label.trim(),
      hint: draft.hint ?? '',
      order: draft.order ?? shots.length,
    };
    const ok = draft._id
      ? await run(() => api.updateSampleShot(draft._id!, body), 'Slot saved.')
      : await run(() => api.createSampleShot(body), 'Slot added.');
    if (ok) setDraft(null);
  }

  return (
    <div className="vu-wrap">
      <div className="vu-kicker">Tools</div>
      <h1 className="vu-h1">Sample photos</h1>
      <p className="vu-lede" style={{ maxWidth: '62ch' }}>
        The reference shots vendors see on the photo standard page. A slot with no photo
        shows as an empty frame, so it is safe to fill them one at a time. Images are
        resized here before upload — upload the best version you have.
      </p>
      <Banner error={error} note={note} />

      <div className="vu-grid vu-grid-3" style={{ marginTop: 'var(--space-5)' }}>
        {shots.map((shot) => (
          <div key={shot._id} className="vu-card" style={{ padding: 'var(--space-4)' }}>
            <div
              style={{
                aspectRatio: '1 / 1',
                borderRadius: 'var(--radius-md)',
                border: shot.imageUrl ? '1px solid var(--border-subtle)' : '1.5px dashed var(--border-strong)',
                background: 'var(--surface-sunken)',
                overflow: 'hidden',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              {shot.imageUrl ? (
                <img
                  src={shot.imageUrl}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              ) : (
                <span className="vu-subtle" style={{ fontSize: 'var(--fs-xs)' }}>No photo yet</span>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 'var(--space-3)' }}>
              <span style={{ fontWeight: 'var(--fw-bold)', color: 'var(--text-strong)' }}>{shot.label}</span>
              <span className="vu-subtle" style={{ fontSize: 'var(--fs-xs)' }}>
                {shot.imageUrl ? kb(shot.imageBytes) : shot.slug}
              </span>
              <span style={{ flex: 1 }} />
              <IconBtn
                label="Delete slot"
                danger
                onClick={() => {
                  void (async () => {
                    const ok = await confirm({
                      title: `Delete the “${shot.label}” slot?`,
                      body: 'The slot and its photo are removed from the photo standard page. This cannot be undone.',
                      confirmLabel: 'Delete slot',
                      destructive: true,
                    });
                    if (ok) await run(() => api.deleteSampleShot(shot._id));
                  })();
                }}
              >
                ×
              </IconBtn>
            </div>
            <p className="vu-subtle" style={{ margin: '4px 0 var(--space-3)', fontSize: 'var(--fs-xs)', lineHeight: 1.5 }}>
              {shot.hint}
            </p>

            <input
              ref={(el) => {
                inputs.current[shot._id] = el;
              }}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) void upload(shot, file);
              }}
            />
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="vu-btn vu-btn-ghost"
                disabled={busy === shot._id}
                onClick={() => inputs.current[shot._id]?.click()}
              >
                {busy === shot._id ? 'Uploading…' : shot.imageUrl ? 'Replace photo' : 'Upload photo'}
              </button>
              <button type="button" className="vu-btn vu-btn-ghost" onClick={() => setDraft(shot)}>
                Edit
              </button>
              {shot.imageUrl && (
                <button
                  type="button"
                  className="vu-btn vu-btn-ghost"
                  onClick={() => {
                    void (async () => {
                      const ok = await confirm({
                        title: `Remove the ${shot.label} photo?`,
                        body: 'The slot stays; vendors see an empty frame again until you upload another.',
                        confirmLabel: 'Remove photo',
                        destructive: true,
                      });
                      if (ok) await run(() => api.removeSampleShotImage(shot._id), 'Photo removed.');
                    })();
                  }}
                >
                  Remove photo
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        className="vu-btn"
        style={{ marginTop: 'var(--space-5)' }}
        onClick={() => setDraft({ order: shots.length })}
      >
        + New slot
      </button>

      <EditModal
        open={!!draft}
        onClose={() => setDraft(null)}
        title={draft?._id ? 'Edit slot' : 'New slot'}
        description="The label and hint printed under the photo on the vendor page."
        footer={
          <>
            <button type="button" className="vu-btn vu-btn-ghost" onClick={() => setDraft(null)}>Cancel</button>
            <button type="button" className="vu-btn" onClick={save}>Save</button>
          </>
        }
      >
        {draft && (
          <>
            <Field label="Label">
              <Text value={draft.label ?? ''} onChange={(label) => setDraft({ ...draft, label })} />
            </Field>
            <Field label="Slug" hint="Leave blank to derive it from the label.">
              <Text value={draft.slug ?? ''} onChange={(slug) => setDraft({ ...draft, slug })} />
            </Field>
            <Field label="Hint">
              <Text value={draft.hint ?? ''} onChange={(hint) => setDraft({ ...draft, hint })} />
            </Field>
          </>
        )}
      </EditModal>

      {dialog}
    </div>
  );
}
